import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  FileText,
  Newspaper,
  Users,
  Star,
  DollarSign,
  Trophy,
  TrendingUp,
  ShieldAlert,
  Settings,
  Megaphone,
  ArrowRightLeft,
  UserCog,
  Menu,
  X,
  Bell,
  ChevronDown,
  LogOut,
  User,
  KeyRound,
  Tag,
  ImageIcon,
  User2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSidebarStore } from '@/stores/useStore';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { getPendingReviews, getPendingPayouts } from '@/data/mock';
import { useAuth } from '@/authContext/AuthContext';

const navigation = [
  {
    group: 'OVERVIEW',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    ],
  },
  {
    group: 'CONTENT',
    items: [
      { name: 'Prop Firms', href: '/dashboard/firms', icon: Building2 },
      { name: 'Unlisted Firms', href: '/dashboard/firms/unlisted', icon: ShieldAlert },
      { name: 'Exclusive Offers', href: '/dashboard/offers', icon: Tag },
      { name: 'Media Library', href: '/dashboard/media', icon: ImageIcon },
      { name: 'Blog & Resources', href: '/dashboard/blog', icon: FileText },
      { name: 'News', href: '/dashboard/news', icon: Newspaper },
    ],
  },
  {
    group: 'COMMUNITY',
    items: [
      { name: 'Users', href: '/dashboard/users', icon: Users },
      { name: 'Reviews', href: '/dashboard/reviews', icon: Star },
      { name: 'Verified Payouts', href: '/dashboard/payouts', icon: DollarSign,},
    ],
  },
  {
    group: 'SYSTEM',
    items: [
      { name: 'Announcements', href: '/dashboard/announcements', icon: Megaphone },
      { name: 'Site Settings', href: '/dashboard/settings', icon: Settings },
      { name: 'Redirects', href: '/dashboard/redirects', icon: ArrowRightLeft },
    ],
  },
];

const SidebarContent = ({ isCollapsed }) => {
  const location = useLocation();

  return (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex h-[60px] items-center border-b border-white/10 px-4">
        <Link
          to="/dashboard"
          className="flex items-center gap-2"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10">
            <span className="text-lg font-bold text-white">PH</span>
          </div>
          {!isCollapsed && (
            <span className="text-sm font-semibold text-white">
              PropFirmHouse Admin
            </span>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-6">
          {navigation.map((section) => (
            <div key={section.group}>
              {!isCollapsed && (
                <h3 className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">
                  {section.group}
                </h3>
              )}
              <ul className="space-y-1">
                {section.items.map((item) => {
                  const isActive = location.pathname === item.href || 
                    (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
                  const Icon = item.icon;

                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={cn(
                          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all',
                          isActive
                            ? 'bg-white/12 text-white font-medium'
                            : 'text-slate-300 hover:bg-white/6 hover:text-white',
                          isCollapsed && 'justify-center'
                        )}
                      >
                        <Icon className={cn('h-5 w-5', isCollapsed ? '' : 'flex-shrink-0')} />
                        {!isCollapsed && (
                          <>
                            <span className="flex-1">{item.name}</span>
                            {item.badge > 0 && (
                              <Badge
                                variant="destructive"
                                className="h-5 min-w-[20px] px-1.5 text-[10px] bg-red-500 hover:bg-red-500"
                              >
                                {item.badge}
                              </Badge>
                            )}
                          </>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </ScrollArea>

      {/* User section */}
      <div className="border-t border-white/10 p-4">
        <div className={cn('flex items-center gap-3', isCollapsed && 'justify-center')}>
          <Avatar className="h-8 w-8">
            <AvatarImage src="https://api.dicebear.com/7.x/avataaars/svg?seed=John" />
            <AvatarFallback>JA</AvatarFallback>
          </Avatar>
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">John Admin</p>
              <p className="text-xs text-slate-400">Super Admin</p>
            </div>
          )}
          {!isCollapsed && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-slate-400 hover:text-white hover:bg-white/10"
              onClick={() => {}}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export const AdminLayout = () => {
  const { isCollapsed, toggleSidebar } = useSidebarStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const {logout}=useAuth()

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          'hidden lg:flex flex-col bg-[hsl(var(--sidebar-bg))] border-r border-slate-700 transition-all duration-300',
          isCollapsed ? 'w-16' : 'w-64'
        )}
      >
        <SidebarContent isCollapsed={isCollapsed} />
      </aside>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-slate-900/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-[hsl(var(--sidebar-bg))] shadow-xl">
            <div className="absolute top-4 right-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(false)}
                className="text-white hover:bg-white/10"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <SidebarContent isCollapsed={false} />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-[60px] items-center justify-between border-b border-slate-200 bg-white px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                if (window.innerWidth < 1024) {
                  setMobileMenuOpen(true);
                } else {
                  toggleSidebar();
                }
              }}
              className="text-slate-600 hover:text-slate-900"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>

          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="relative text-slate-600 hover:text-slate-900"
            >
              <Bell className="h-5 w-5" />
              <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 px-2"
                >
                  <User2/>
                  <ChevronDown className="h-4 w-4 text-slate-600" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/dashboard/profile')}>
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/profile')}>
                  <KeyRound className="mr-2 h-4 w-4" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="text-red-600"
                  onClick={() => logout()}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-slate-50 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
